import { useCallback, useMemo } from 'react';
import { match } from 'ts-pattern';
import { DateTime, Duration } from 'luxon';
import { useSelector } from 'react-redux';
import { useLocalStorage } from '../../../../utils/storage/localstorage/use-local-storage';
import { LocalStorageKey } from '../../../../utils/storage/localstorage/localstorage';
import type { IAlertData } from '../../../../dotcms/dotcms-models/dotcms-alert';
import { useFetchIssuerDotcmsContentWithDefaultFallback } from '../../../../dotcms/use-fetch-from-dotcms-with-default-fallback';
import { GlobalLoadingStateOperation } from '../../global-loading-context/global-loading-context';
import { useBuildersToProcessGqlData } from '../../../../dotcms/use-builders-to-process-gql-data';
import type { Page } from '../../../../app/page-url-path';
import {
  AlertDisplayConditionTime,
  AlertDisplayConditionUserState,
  AlertTriggerToDisplay,
} from '../../../../dotcms/dotcms-models/dotcms-alert-enums';
import { UserEnrollmentState } from '../../../../hooks/use-get-user-states-in-enrollment/use-get-user-enrollment-state';
import { UserAuthenticationState } from '../../../../hooks/use-get-user-states-in-enrollment/use-get-user-auth-state';
import { replaceVariablesInHtml } from '../../../../dotcms/dotcms-models/converters-from-dotcms';
import { useDateFormatter } from '../../../../hooks/useDateFormatter/useDateFormatter';
import { useIsInitialDataLoaded } from '../../../initial-page-load-handler/initial-page-load-handler';
import {
  EnrollmentPeriodsConcurrency,
  useGetEnrollmentPlan,
} from '../../../../hooks/use-get-enrollment-plan/use-get-enrollment-plan';
import { UserEnrolledState } from '../../../../hooks/use-get-user-states-in-enrollment/use-get-user-enrolled-state';
import { AlertsWrapperGqlHelper } from '../../../../dotcms/dotcms-models/dotcms-alerts-wrapper';
import useGetPeriodsOpenInfo from '../../../../hooks/use-get-periods-info/use-get-periods-open-info';
import { useGetExpressionOfInterestInfo } from '../../../../hooks/use-get-expression-of-interest-info';
import { useGetUserStatesInEnrollment } from '../../../../hooks/use-get-user-states-in-enrollment';
import { issuerSelector } from '../../../../selectors';

const useAlertsStorage = () => {
  const customDynamicVariableDurationFromName = 'ce_custom_duration_from';
  const customDynamicVariableDurationFrom = `{{${customDynamicVariableDurationFromName}}}`;
  const customDynamicVariableDurationToName = 'ce_custom_duration_to';
  const customDynamicVariableDurationTo = `{{${customDynamicVariableDurationToName}}}`;

  const ignoredCustomDynamicVariables = useMemo(
    () => [
      customDynamicVariableDurationFromName,
      customDynamicVariableDurationToName,
    ],
    [
      customDynamicVariableDurationFromName,
      customDynamicVariableDurationToName,
    ],
  );

  const { createGqlQueryForSingle, processGqlDataAsSingle, contentTypeName } =
    useBuildersToProcessGqlData(AlertsWrapperGqlHelper);
  const { data } = useFetchIssuerDotcmsContentWithDefaultFallback({
    buildGqlSubQuery: createGqlQueryForSingle,
    processResponse: processGqlDataAsSingle,
    loadingStateOperationName:
      GlobalLoadingStateOperation.ALERT_BANNER_DOTCMS_CONTENT_FETCH,
    contentTypeName,
    delayedCustomDynamicVariables: ignoredCustomDynamicVariables,
  });

  const { getValueFromLocalStorage, setValueToLocalStorage } = useLocalStorage(
    LocalStorageKey.ALERT_BANNERS,
  );
  const { formatIsoTime } = useDateFormatter();
  const initialDataLoaded = useIsInitialDataLoaded();

  const { isPlanInSuspensionState } = useSelector(issuerSelector);
  const enrollmentPlan = useGetEnrollmentPlan();
  const {
    isEnrollmentOpen,
    isEnrollmentOrScheduledEnrollmentOpen,
    isScheduledEnrollmentOpen,
    isEsppInternalEnrollmentOpen,
    isCashlessEnrollmentOpen,
    isQuietPeriodBetweenEsppAndCashless,
  } = useGetPeriodsOpenInfo();

  const { isBeforeEoiDeadline, isAfterEoiDeadline } =
    useGetExpressionOfInterestInfo();

  const userStates = useGetUserStatesInEnrollment();

  const allAlerts = useMemo(
    () => (data ? data.standardAlerts.concat(data.customAlerts) : null),
    [data],
  );

  const formatDate = useCallback(
    (date: DateTime | undefined) =>
      (date ? formatIsoTime(date.toString()) : '-'),
    [formatIsoTime],
  );

  const isAlertInDateRange = useCallback((alert: IAlertData) => {
    const currentTime = DateTime.now();
    const from = alert.durationFrom?.toString();
    const to = alert.durationTo?.toString();
    return (
      (!alert.durationFrom ||
        (from && DateTime.fromISO(from, { setZone: true }) <= currentTime)) &&
      (!alert.durationTo ||
        (to && DateTime.fromISO(to, { setZone: true }) >= currentTime))
    );
  }, []);

  const matchAlertAnyTriggerConditions = useCallback(
    (alert: IAlertData) => {
      if (!initialDataLoaded) {
        return false;
      }

      const conditionsResult = alert.triggerToDisplayAlert.map((condition) =>
        match(condition)
          .with(
            AlertTriggerToDisplay.LOGGED_IN,
            () =>
              userStates.userAuthState ===
              UserAuthenticationState.UserAuthenticated,
          )
          .with(
            AlertTriggerToDisplay.LOGGED_OUT,
            () =>
              userStates.userAuthState !==
              UserAuthenticationState.UserAuthenticated,
          )
          .exhaustive());

      return conditionsResult.includes(true);
    },
    [initialDataLoaded, userStates.userAuthState],
  );

  const matchAlertAnyTimeConditions = useCallback(
    (alert: IAlertData) => {
      const conditionsResult = alert.displayConditionsTime
        .filter(
          (condition) =>
            !isPlanInSuspensionState ||
            [
              AlertDisplayConditionTime.SHOW_ALWAYS,
              AlertDisplayConditionTime.CUSTOM_DATE,
            ].includes(condition),
        )
        .map((condition) =>
          match(condition)
            .with(AlertDisplayConditionTime.SHOW_ALWAYS, () => true)
            .with(AlertDisplayConditionTime.CUSTOM_DATE, () =>
              isAlertInDateRange(alert))
            .with(
              AlertDisplayConditionTime.ALL_ENROLLMENTS_FINISHED_SCHEDULED_ENROLLMENT_OPEN,
              () => !isEnrollmentOpen && isScheduledEnrollmentOpen,
            )
            .with(
              AlertDisplayConditionTime.ALL_ENROLLMENTS_OPEN_SAME_PERIODS,
              () =>
                enrollmentPlan.concurrency ===
                  EnrollmentPeriodsConcurrency.FULLY_CONCURRENT_INTERNAL &&
                isEnrollmentOpen,
            )
            .with(
              AlertDisplayConditionTime.ALL_ENROLLMENTS_FINISHED_SAME_PERIODS,
              () =>
                enrollmentPlan.concurrency ===
                  EnrollmentPeriodsConcurrency.FULLY_CONCURRENT_INTERNAL &&
                !isEnrollmentOrScheduledEnrollmentOpen,
            )
            .with(
              AlertDisplayConditionTime.ESPP_ONLY_ENROLLMENT_OPEN_DIFFERENT_PERIODS,
              () =>
                enrollmentPlan.concurrency !==
                  EnrollmentPeriodsConcurrency.FULLY_CONCURRENT_INTERNAL &&
                isEsppInternalEnrollmentOpen &&
                !isCashlessEnrollmentOpen,
            )
            .with(
              // eslint-disable-next-line max-len
              AlertDisplayConditionTime.ESPP_ENROLLMENT_FINISHED_CASHLESS_ENROLLMENT_NOT_STARTED_DIFFERENT_PERIODS,
              () =>
                enrollmentPlan.concurrency !==
                  EnrollmentPeriodsConcurrency.FULLY_CONCURRENT_INTERNAL &&
                !isEsppInternalEnrollmentOpen &&
                !isCashlessEnrollmentOpen &&
                isQuietPeriodBetweenEsppAndCashless,
            )
            .with(
              AlertDisplayConditionTime.ESPP_AND_CASHLESS_ENROLLMENTS_OPEN_DIFFERENT_PERIODS,
              () =>
                enrollmentPlan.concurrency !==
                  EnrollmentPeriodsConcurrency.FULLY_CONCURRENT_INTERNAL &&
                isEsppInternalEnrollmentOpen &&
                isCashlessEnrollmentOpen,
            )
            .with(
              AlertDisplayConditionTime.CASHLESS_ONLY_ENROLLMENT_OPEN_DIFFERENT_PERIODS,
              () =>
                enrollmentPlan.concurrency !==
                  EnrollmentPeriodsConcurrency.FULLY_CONCURRENT_INTERNAL &&
                !isEsppInternalEnrollmentOpen &&
                isCashlessEnrollmentOpen,
            )
            .with(
              AlertDisplayConditionTime.ALL_ENROLLMENTS_FINISHED_DIFFERENT_PERIODS,
              () =>
                enrollmentPlan.concurrency !==
                  EnrollmentPeriodsConcurrency.FULLY_CONCURRENT_INTERNAL &&
                !isEsppInternalEnrollmentOpen &&
                !isCashlessEnrollmentOpen &&
                !isQuietPeriodBetweenEsppAndCashless &&
                !isEnrollmentOrScheduledEnrollmentOpen,
            )
            .with(AlertDisplayConditionTime.EOI_OPEN, () => isBeforeEoiDeadline)
            .with(
              AlertDisplayConditionTime.EOI_DEADLINE_EXCEEDED_BUT_ENROLLMENT_OPEN,
              () => isAfterEoiDeadline,
            )
            .exhaustive());
      return conditionsResult.includes(true);
    },
    [
      enrollmentPlan,
      isAlertInDateRange,
      isPlanInSuspensionState,
      isEnrollmentOpen,
      isEsppInternalEnrollmentOpen,
      isCashlessEnrollmentOpen,
      isQuietPeriodBetweenEsppAndCashless,
      isBeforeEoiDeadline,
      isAfterEoiDeadline,
      isScheduledEnrollmentOpen,
      isEnrollmentOrScheduledEnrollmentOpen,
    ],
  );

  const matchAlertAllUserStateConditions = useCallback(
    (alert: IAlertData) => {
      if (!alert.displayConditionsUserState) {
        return true;
      }

      const userStateForNewEnrollmentFlow =
        userStates.userFlowState.userStatesInPeriodOpOrPp;

      const conditionsResult = alert.displayConditionsUserState.map(
        (condition) =>
          (condition === AlertDisplayConditionUserState.USER_PLAN_SUSPENDED
            ? isPlanInSuspensionState
            : userStates.userAuthState ===
                UserAuthenticationState.UserNotAuthenticated ||
              match(condition)
                .with(
                  AlertDisplayConditionUserState.USER_NOT_ENROLLED,
                  () =>
                    userStateForNewEnrollmentFlow?.userEnrollmentState ===
                    UserEnrollmentState.UserNotEnrolled,
                )
                .with(
                  AlertDisplayConditionUserState.USER_ENROLLED,
                  () =>
                    userStateForNewEnrollmentFlow?.userEnrolledState ===
                    UserEnrolledState.UserEnrolled,
                )
                .with(
                  AlertDisplayConditionUserState.USER_ENROLLED_IN_ESPP_AND_ELIGIBLE_FOR_CASHLESS,
                  () =>
                    userStateForNewEnrollmentFlow?.userEnrollmentState ===
                    UserEnrollmentState.UserAlreadyEnrolledInESPP,
                )
                .with(
                  AlertDisplayConditionUserState.USER_WITH_PURCHASE_IN_PAST_YEAR,
                  // TODO: hasTaxDocumentsThisYear from BE when it's ready
                  () => false,
                )
                .exhaustive()),
      );

      return conditionsResult.includes(true);
    },
    [
      isPlanInSuspensionState,
      userStates.userAuthState,
      userStates.userFlowState.userStatesInPeriodOpOrPp,
    ],
  );

  const matchAlertPageCondition = useCallback(
    (alert: IAlertData, page: Page) =>
      alert.displayConditionsPage?.includes(page) ?? false,
    [],
  );

  const getClosedAlertsFromStorage = useCallback((): string[] => {
    const value = getValueFromLocalStorage({
      maxAge: Duration.fromObject({ days: 1 }),
    });

    return value?.removedAlerts ?? [];
  }, [getValueFromLocalStorage]);

  const removeAlert = useCallback(
    (alert: IAlertData) => {
      const closedAlertIds = getClosedAlertsFromStorage();
      if (alert.identifier) {
        closedAlertIds.push(alert.identifier);
      }
      const clearedClosedAlerts = allAlerts
        ? closedAlertIds.filter((id) =>
          allAlerts.find((x) => x.identifier === id))
        : closedAlertIds;
      setValueToLocalStorage({ removedAlerts: clearedClosedAlerts });
    },
    [allAlerts, getClosedAlertsFromStorage, setValueToLocalStorage],
  );

  // TODO: Add sorting?
  const getUserAlertsByConditions = useCallback(
    (page: Page): IAlertData[] => {
      if (!allAlerts) {
        return [];
      }

      const closedAlertIds = getClosedAlertsFromStorage();
      const alertsByConditions = allAlerts.filter(
        (alert) =>
          !closedAlertIds.includes(alert.identifier ?? '') &&
          matchAlertAnyTriggerConditions(alert) &&
          matchAlertAnyTimeConditions(alert) &&
          matchAlertAllUserStateConditions(alert) &&
          matchAlertPageCondition(alert, page),
      );

      const preparedAlerts = alertsByConditions.map((alert) => {
        const alertText = alert
          ? replaceVariablesInHtml(alert.text, {
            [customDynamicVariableDurationFrom]: formatDate(
              alert.durationFrom,
            ),
            [customDynamicVariableDurationTo]: formatDate(alert.durationTo),
          })
          : undefined;
        return alertText ? { ...alert, text: alertText } : alert;
      });
      return preparedAlerts;
    },
    [
      allAlerts,
      customDynamicVariableDurationFrom,
      customDynamicVariableDurationTo,
      formatDate,
      getClosedAlertsFromStorage,
      matchAlertAnyTriggerConditions,
      matchAlertAnyTimeConditions,
      matchAlertAllUserStateConditions,
      matchAlertPageCondition,
    ],
  );

  return useMemo(
    () => ({
      get: getUserAlertsByConditions,
      remove: removeAlert,
    }),
    [getUserAlertsByConditions, removeAlert],
  );
};

export default useAlertsStorage;
